import { useEffect, useState } from 'react';
import { useTranslations, useLocale } from 'next-intl';

export interface FormattedReview {
  review_id: string;
  submission_date: string;
  title: string;
  name: string;
  content: string;
  rating: number;
  max_rating: number;
  location?: string;
  gender?: string;
  language: string;
  locale: string;
  photos: string[];
  videos: string[];
  age?: string;
  commenter_location?: string;
  responder_name?: string;
  response_department?: string;
  category_ratings?: {
    appearance?: string;
    comfort?: string;
    fit?: string;
    quality?: string;
    value?: string;
  };
}

interface ReviewApiResponse {
  reviews: FormattedReview[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
  filters: {
    language: string;
    locale: string;
  };
}

interface UseReviewsOptions {
  limit?: number;
  page?: number;
  requireContent?: boolean;
  enabled?: boolean;
  sort?: string | string[]; // accepts 'date,length' or ['date','length']
  order?: 'asc' | 'dsc' | 'desc';
  minContentLength?: number;
  maxContentLength?: number;
  minRating?: number;
  maxRating?: number;
  hasMedia?: boolean;
}

export function useReviews(options: UseReviewsOptions = {}) {
  const t = useTranslations('common');
  const i18nLanguage = useLocale();
  const {
    limit = 9,
    page = 1,
    requireContent = false,
    enabled = true,
    sort,
    order = 'dsc',
    minContentLength,
    maxContentLength,
    minRating,
    maxRating,
    hasMedia,
  } = options;

  const [reviews, setReviews] = useState<FormattedReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<
    ReviewApiResponse['pagination'] | undefined
  >(undefined);
  const [currentPage, setCurrentPage] = useState(page);

  useEffect(() => {
    if (!enabled) return;

    const fetchReviews = async () => {
      try {
        setLoading(true);
        setError(null);

        const locale = i18nLanguage || 'en';
        const [language] = locale.split('-');

        const apiLocale = locale.includes('_')
          ? locale
          : `${language}_${language.toUpperCase()}`;

        const url = new URL('/api/reviews', window.location.origin);
        url.searchParams.append('locale', apiLocale);
        url.searchParams.append('language', language);
        url.searchParams.append('limit', limit.toString());
        url.searchParams.append('page', currentPage.toString());
        if (requireContent) {
          url.searchParams.append('require_content', 'true');
        }
        // Sorting
        if (Array.isArray(sort)) {
          sort
            .filter(Boolean)
            .forEach((key) => url.searchParams.append('sort', key));
        } else if (typeof sort === 'string' && sort.trim() !== '') {
          // allow comma-separated
          sort
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
            .forEach((key) => url.searchParams.append('sort', key));
        }
        if (order) {
          url.searchParams.append('order', order);
        }
        // Filters
        if (typeof minContentLength === 'number') {
          url.searchParams.append(
            'min_content_length',
            String(minContentLength),
          );
        }
        if (typeof maxContentLength === 'number') {
          url.searchParams.append(
            'max_content_length',
            String(maxContentLength),
          );
        }
        if (typeof minRating === 'number') {
          url.searchParams.append('min_rating', String(minRating));
        }
        if (typeof maxRating === 'number') {
          url.searchParams.append('max_rating', String(maxRating));
        }
        if (typeof hasMedia === 'boolean') {
          url.searchParams.append('has_media', String(hasMedia));
        }

        const response = await fetch(url.href);

        if (!response.ok) {
          throw new Error(`Failed to fetch reviews: ${response.status}`);
        }

        const data: ReviewApiResponse = await response.json();

        if (currentPage === 1) {
          setReviews(data.reviews);
        } else {
          setReviews((prev) => [...prev, ...data.reviews]);
        }

        setPagination(data.pagination);
      } catch (err) {
        console.error('Error fetching reviews:', err);
        setError(t('reviewsSection.text.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [
    i18nLanguage,
    limit,
    currentPage,
    requireContent,
    enabled,
    sort,
    order,
    minContentLength,
    maxContentLength,
    minRating,
    maxRating,
    hasMedia,
    t,
  ]);

  useEffect(() => {
    setCurrentPage(1);
    setReviews([]);
  }, [
    limit,
    requireContent,
    sort,
    order,
    minContentLength,
    maxContentLength,
    minRating,
    maxRating,
    hasMedia,
  ]);

  const loadMore = () => {
    if (pagination?.has_next && !loading) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const reset = () => {
    setCurrentPage(1);
    setReviews([]);
  };

  return {
    reviews,
    loading,
    error,
    pagination,
    loadMore,
    reset,
    hasMore: pagination?.has_next || false,
  };
}
